import { logger } from '../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { emergencyDataService } from './emergency-data-service.js'
import type { EmergencyDataType, AnyEmergencyFacility } from './emergency-types.js'

const log = logger.create('emergency-layers')

interface EmergencyLayerConfig {
  dataType: EmergencyDataType
  sourceId: string
  layerId: string
  clusterLayerId: string
  clusterCountLayerId: string
  color: string
  icon: string
  label: string
}

const LAYER_CONFIGS: EmergencyLayerConfig[] = [
  {
    dataType: 'hospitals',
    sourceId: 'emergency-hospitals',
    layerId: 'emergency-hospitals-points',
    clusterLayerId: 'emergency-hospitals-clusters',
    clusterCountLayerId: 'emergency-hospitals-cluster-count',
    color: '#dc2626',
    icon: 'local_hospital',
    label: 'Hospital',
  },
  {
    dataType: 'fire-stations',
    sourceId: 'emergency-fire-stations',
    layerId: 'emergency-fire-stations-points',
    clusterLayerId: 'emergency-fire-stations-clusters',
    clusterCountLayerId: 'emergency-fire-stations-cluster-count',
    color: '#ea580c',
    icon: 'local_fire_department',
    label: 'Fire Station',
  },
  {
    dataType: 'law-enforcement',
    sourceId: 'emergency-law-enforcement',
    layerId: 'emergency-law-enforcement-points',
    clusterLayerId: 'emergency-law-enforcement-clusters',
    clusterCountLayerId: 'emergency-law-enforcement-cluster-count',
    color: '#2563eb',
    icon: 'shield',
    label: 'Law Enforcement',
  },
  {
    dataType: 'usfs-offices',
    sourceId: 'emergency-usfs-offices',
    layerId: 'emergency-usfs-offices-points',
    clusterLayerId: 'emergency-usfs-offices-clusters',
    clusterCountLayerId: 'emergency-usfs-offices-cluster-count',
    color: '#16a34a',
    icon: 'park',
    label: 'USFS Office',
  },
]

const MIN_ZOOM = 8
const CLUSTER_MAX_ZOOM = 11
const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

const EMERGENCY_ICONS: Record<EmergencyDataType, { icon: string; color: string }> = {
  hospitals: { icon: 'local_hospital', color: '#dc2626' },
  'fire-stations': { icon: 'local_fire_department', color: '#ea580c' },
  'law-enforcement': { icon: 'shield', color: '#2563eb' },
  'usfs-offices': { icon: 'park', color: '#16a34a' },
}

function registerMarkerIcon(
  map: maplibregl.Map,
  imageId: string,
  iconName: string,
  color: string
): Promise<void> {
  if (map.hasImage(imageId)) return Promise.resolve()

  const dpr = window.devicePixelRatio || 1
  const size = 28
  const canvas = document.createElement('canvas')
  canvas.width = size * dpr
  canvas.height = size * dpr
  const ctx = canvas.getContext('2d')!
  ctx.scale(dpr, dpr)

  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - 2

  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 2
  ctx.stroke()

  return new Promise<void>(resolve => {
    if (!document.fonts.check('16px "Material Symbols Outlined"')) {
      document.fonts
        .load('16px "Material Symbols Outlined"')
        .then(() => {
          drawIconAndRegister()
        })
        .catch(() => {
          drawIconAndRegister()
        })
    } else {
      drawIconAndRegister()
    }

    function drawIconAndRegister(): void {
      ctx.font = '14px "Material Symbols Outlined"'
      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(iconName, cx, cy)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      map.addImage(imageId, imageData, { pixelRatio: dpr })
      resolve()
    }
  })
}

function buildPopupHtml(facility: AnyEmergencyFacility): string {
  const parts: string[] = []
  parts.push(`<strong>${facility.name}</strong>`)

  if (facility.type === 'hospital') {
    const tags: string[] = []
    if (facility.traumaLevel !== 'NOT_DESIGNATED' && facility.traumaLevel !== 'UNKNOWN') {
      tags.push(facility.traumaLevel.replace('_', ' '))
    }
    if (facility.helipad) tags.push('Helipad')
    if (facility.beds) tags.push(`${facility.beds} beds`)
    if (tags.length > 0) parts.push(`<br><em>${tags.join(' · ')}</em>`)
  }

  if (facility.type === 'usfs-office') {
    if (facility.forestName) parts.push(`<br>${facility.forestName}`)
    if (facility.districtName) parts.push(` — ${facility.districtName}`)
  }

  if (facility.type === 'law-enforcement') {
    if (facility.agencyType !== 'OTHER') {
      parts.push(`<br><em>${facility.agencyType.replace('_', ' ')}</em>`)
    }
  }

  const addr: string[] = []
  if (facility.address) addr.push(facility.address)
  if (facility.city) addr.push(facility.city)
  if (facility.state) addr.push(facility.state)
  if (addr.length > 0) parts.push(`<br>${addr.join(', ')}`)

  if (facility.phone) {
    parts.push(`<br><a href="tel:${facility.phone}">${facility.phone}</a>`)
  }

  return parts.join('')
}

class EmergencyLayerService {
  private map: maplibregl.Map | null = null
  private enabledLayers = new Set<EmergencyDataType>()
  private popup: maplibregl.Popup | null = null
  private moveEndHandler: (() => void) | null = null
  private pendingUpdate: number | null = null
  private iconsReady: Promise<void> = Promise.resolve()

  get allLayerConfigs(): ReadonlyArray<EmergencyLayerConfig> {
    return LAYER_CONFIGS
  }

  isEnabled(dataType: EmergencyDataType): boolean {
    return this.enabledLayers.has(dataType)
  }

  register(map: maplibregl.Map): void {
    this.map = map
    this.moveEndHandler = () => this.onMoveEnd()
    this.map.on('moveend', this.moveEndHandler)

    const iconPromises = LAYER_CONFIGS.map(config => {
      const iconConf = EMERGENCY_ICONS[config.dataType]
      return registerMarkerIcon(
        map,
        `emergency-icon-${config.dataType}`,
        iconConf.icon,
        iconConf.color
      )
    })
    this.iconsReady = Promise.all(iconPromises).then(() => {})

    for (const config of LAYER_CONFIGS) {
      this.map.on('click', config.layerId, (e: maplibregl.MapLayerMouseEvent) => {
        this.handleClick(e, config)
      })
      this.map.on('mouseenter', config.layerId, () => {
        if (this.map) this.map.getCanvas().style.cursor = 'pointer'
      })
      this.map.on('mouseleave', config.layerId, () => {
        if (this.map) this.map.getCanvas().style.cursor = ''
      })
    }
  }

  unregister(): void {
    if (this.pendingUpdate) {
      cancelAnimationFrame(this.pendingUpdate)
      this.pendingUpdate = null
    }
    for (const config of LAYER_CONFIGS) {
      this.disable(config.dataType)
    }
    if (this.map && this.moveEndHandler) {
      this.map.off('moveend', this.moveEndHandler)
    }
    this.map = null
  }

  enable(dataType: EmergencyDataType): void {
    if (!this.map || this.enabledLayers.has(dataType)) return
    this.enabledLayers.add(dataType)

    const config = LAYER_CONFIGS.find(c => c.dataType === dataType)
    if (!config) return

    void this.iconsReady.then(() => {
      this.addLayersForConfig(config)
      this.updateDataForConfig(config)
    })
  }

  disable(dataType: EmergencyDataType): void {
    if (!this.map) return
    this.enabledLayers.delete(dataType)

    const config = LAYER_CONFIGS.find(c => c.dataType === dataType)
    if (!config) return

    this.removeLayersForConfig(config)
  }

  toggle(dataType: EmergencyDataType): void {
    if (this.enabledLayers.has(dataType)) this.disable(dataType)
    else this.enable(dataType)
  }

  reAdd(): void {
    if (!this.map) return
    for (const config of LAYER_CONFIGS) {
      if (this.enabledLayers.has(config.dataType)) {
        if (!this.map.getSource(config.sourceId)) {
          this.addLayersForConfig(config)
          this.updateDataForConfig(config)
        }
      }
    }
  }

  private addLayersForConfig(config: EmergencyLayerConfig): void {
    if (!this.map) return

    if (!this.map.getSource(config.sourceId)) {
      this.map.addSource(config.sourceId, {
        type: 'geojson',
        data: EMPTY_GEOJSON,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: 50,
      })
    }

    const groupId = `emergency-${config.dataType}`

    if (!this.map.getLayer(config.clusterLayerId)) {
      addOverlayLayer(
        this.map,
        {
          id: config.clusterLayerId,
          type: 'circle',
          source: config.sourceId,
          filter: ['has', 'point_count'],
          minzoom: MIN_ZOOM,
          paint: {
            'circle-color': config.color,
            'circle-opacity': 0.7,
            'circle-radius': ['step', ['get', 'point_count'], 15, 10, 20, 50, 25],
          },
        },
        groupId
      )
    }

    if (!this.map.getLayer(config.clusterCountLayerId)) {
      addOverlayLayer(
        this.map,
        {
          id: config.clusterCountLayerId,
          type: 'symbol',
          source: config.sourceId,
          filter: ['has', 'point_count'],
          minzoom: MIN_ZOOM,
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-size': 12,
          },
          paint: {
            'text-color': '#ffffff',
          },
        },
        groupId
      )
    }

    if (!this.map.getLayer(config.layerId)) {
      addOverlayLayer(
        this.map,
        {
          id: config.layerId,
          type: 'symbol',
          source: config.sourceId,
          filter: ['!', ['has', 'point_count']],
          minzoom: MIN_ZOOM,
          layout: {
            'icon-image': `emergency-icon-${config.dataType}`,
            'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 12, 0.8, 14, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
        },
        groupId
      )
    }
  }

  private removeLayersForConfig(config: EmergencyLayerConfig): void {
    if (!this.map) return
    for (const layerId of [config.layerId, config.clusterCountLayerId, config.clusterLayerId]) {
      if (this.map.getLayer(layerId)) this.map.removeLayer(layerId)
    }
    if (this.map.getSource(config.sourceId)) this.map.removeSource(config.sourceId)
  }

  private onMoveEnd(): void {
    if (this.pendingUpdate) cancelAnimationFrame(this.pendingUpdate)
    this.pendingUpdate = requestAnimationFrame(() => {
      this.pendingUpdate = null
      for (const config of LAYER_CONFIGS) {
        if (this.enabledLayers.has(config.dataType)) {
          this.updateDataForConfig(config)
        }
      }
    })
  }

  private updateDataForConfig(config: EmergencyLayerConfig): void {
    if (!this.map) return
    const bounds = this.map.getBounds()
    const south = bounds.getSouth()
    const west = bounds.getWest()
    const north = bounds.getNorth()
    const east = bounds.getEast()

    emergencyDataService
      .queryByBounds(config.dataType, south, west, north, east)
      .then(facilities => {
        if (!this.map) return
        const source = this.map.getSource<maplibregl.GeoJSONSource>(config.sourceId)
        if (!source) return

        const fc: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: facilities.map(f => ({
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [f.lon, f.lat] },
            properties: f as unknown as GeoJSON.GeoJsonProperties,
          })),
        }
        source.setData(fc)
      })
      .catch(err => {
        log.warn(`Failed to update ${config.dataType} data`, err)
      })
  }

  async queryFeaturesAtPoint(
    point: maplibregl.Point
  ): Promise<Array<{ dataType: EmergencyDataType; facility: AnyEmergencyFacility }>> {
    if (!this.map) return []

    const results: Array<{ dataType: EmergencyDataType; facility: AnyEmergencyFacility }> = []

    for (const config of LAYER_CONFIGS) {
      if (!this.enabledLayers.has(config.dataType)) continue

      const unclusteredHits = this.map.getLayer(config.layerId)
        ? this.map.queryRenderedFeatures(point, { layers: [config.layerId] })
        : []

      for (const hit of unclusteredHits) {
        results.push({
          dataType: config.dataType,
          facility: hit.properties as unknown as AnyEmergencyFacility,
        })
      }

      const clusterHits = this.map.getLayer(config.clusterLayerId)
        ? this.map.queryRenderedFeatures(point, { layers: [config.clusterLayerId] })
        : []

      for (const hit of clusterHits) {
        const clusterId = hit.properties.cluster_id as number | undefined
        if (clusterId == null) continue
        const source = this.map.getSource<maplibregl.GeoJSONSource>(config.sourceId)
        if (!source) continue
        try {
          const leaves = await source.getClusterLeaves(clusterId, 20, 0)
          for (const leaf of leaves) {
            if (leaf.properties) {
              results.push({
                dataType: config.dataType,
                facility: leaf.properties as unknown as AnyEmergencyFacility,
              })
            }
          }
        } catch {
          // cluster may have been removed
        }
      }
    }

    return results
  }

  private handleClick(e: maplibregl.MapLayerMouseEvent, _config: EmergencyLayerConfig): void {
    if (!this.map || !e.features || e.features.length === 0) return

    const props = e.features[0].properties
    if (!props) return

    const facility = props as unknown as AnyEmergencyFacility
    this.popup?.remove() // popup is set on prior event handler invocations

    this.popup = new maplibregl.Popup({ maxWidth: '300px', closeButton: true })
      .setLngLat(e.lngLat)
      .setHTML(buildPopupHtml(facility))
      .addTo(this.map)
  }
}

export const emergencyLayerService = new EmergencyLayerService()
